import {Modal} from "antd";
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {readBarcode} from "@lib/zxing";
import {blobToData} from "@lib/utils";
import {ScanBarcodeIcon} from "@components/icons/HomePageIcons";
import {WarningOutlined} from "@ant-design/icons";
import {getWordpressUrl} from "@lib/wordpressVars";
import {useShowIsScanning} from "@components/barcode-read-provider/BarcodeReadProvider";

import example_img from "@images/barcode_example.png";

interface BarcodeFileInputProps {
    isLoggedIn: boolean;
    showDesktopView: boolean;
    toggleTopNavOnClick: boolean;
    onChange: (barcode: string) => void;
};

let inputCount = 0;

const BarcodeFileInput = ({showDesktopView, onChange, toggleTopNavOnClick, isLoggedIn}: BarcodeFileInputProps) => {
    const isMobileDevice = window.navigator.maxTouchPoints > 2;
    const inputRef = useRef<any>(null);
    const [isScanning, setIsScanning] = useState(false);
    const showIsScanning = useShowIsScanning();

    const inputId = useMemo(() => "barcode-camera-input" + (inputCount++), []);

    useEffect(() => {
        const handlerFunc = () => setIsScanning(false);

        if(inputRef && inputRef.current){
            inputRef.current.addEventListener("cancel", handlerFunc);

            return () => {
                inputRef.current.removeEventListener(() => handlerFunc);
            }
        }
    }, [inputRef, setIsScanning])

    const readFileInput = useCallback(async (event) => {
        setIsScanning(true);

        const file = event.target.files[0];
        const imageBase64 = await blobToData(file, true, 2000);
        const barcode = await readBarcode(imageBase64);

        event.target.files = (new DataTransfer()).files;

        if(barcode){
            onChange(barcode);
        }else{
            Modal.warning({
                autoFocusButton: null,
                closable: true,
                maskClosable: true,
                icon: <WarningOutlined />,
                content: (<div>
                    <div><b>Barcode Not Detected</b></div>
                    <div className={'pad-top-10'}>
                        <p>To scan a barcode, make sure the barcode photo is in focus, close to the camera, and oriented right-side-up.</p>
                        <p>
                            Example image: <br/>
                            <img src={getWordpressUrl(example_img)} />
                        </p>
                    </div>
                </div>),
            })
        }

        setIsScanning(false);
    }, [setIsScanning]);


    const onClick = useCallback(() => {
        if(!isLoggedIn){
            Modal.warning({
                autoFocusButton: null,
                closable: true,
                maskClosable: true,
                icon: <WarningOutlined />,
                content: (<div>
                    <div><b>Log In on a Mobile Device</b></div>
                    <div className={'pad-top-10'}>
                        <p>The barcode scanning feature is only available on mobile devices for CovrPrice members. Please log in and try again.</p>
                    </div>
                </div>),
            })
        }else if(!isMobileDevice){
            Modal.warning({
                autoFocusButton: null,
                closable: true,
                maskClosable: true,
                icon: <WarningOutlined />,
                content: (<div>
                    <div><b>Use Your Mobile Device</b></div>
                    <div className={'pad-top-10'}>
                        <p>The barcode scanning feature is only available for users on mobile devices.</p>
                    </div>
                </div>),
            })
        }
    }, [isLoggedIn, isMobileDevice]);

    useEffect(() => {
        showIsScanning(isScanning);
    }, [isScanning]);

    return (
        <>
            {isLoggedIn && isMobileDevice ? (
                <label
                    className="whiteButton scanButton"
                    htmlFor={inputId}
                    data-toggle={toggleTopNavOnClick ? "header-search-overlay" : null}
                    onClick={() => setIsScanning(true)}
                >
                    <ScanBarcodeIcon className={"iconBlack " + (showDesktopView ? "icon60" : "icon48")}/>
                </label>
            ) : (
                <div className="whiteButton" onClick={onClick}>
                    <ScanBarcodeIcon className={"iconBlack " + (showDesktopView ? "icon60" : "icon48")}/>
                </div>
            )}
            <input
                id={inputId}
                type="file"
                accept="image/*"
                capture="environment"
                style={{position: "absolute", zIndex: -1, top: 10, left: -200, visibility: "hidden"}}
                ref={inputRef}
                onChange={readFileInput}
            />
        </>
    );
};

export default BarcodeFileInput;